import type {
  Api,
  Config,
  ContextValue,
  HoneepotEvent,
  HoneepotEventListener,
} from '@/modules/common/context/Honeepot/models';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {HoneepotContext} from '@/modules/common/context/Honeepot/HoneepotContext';
import {wrapHoneepotCustomElement} from '@/modules/common/context/Honeepot/utils';

const SDK_TIMEOUT = 10_000;
const SDK_POLLING_INTERVAL = 200;

declare const window: {
  odysseus?: Api;
  odysseusExitIntentConfig?: Config;
} & Window;

type Props = React.PropsWithChildren<{
  sdkUrl: string;
  config?: any;
}>;

export const HoneepotProvider: React.FunctionComponent<Props> = ({
  sdkUrl,
  children,
  config,
}) => {
  const [api, setApi] = useState<Api | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const sdkLoaded = useRef(false);
  const sdkPollInterval = useRef<number>();
  const eventHandlers = useRef<Map<HoneepotEvent, HoneepotEventListener>>(
    new Map()
  );

  useEffect(
    function clickEventHandling() {
      if (api?.wrapperElement == null) {
        return;
      }
      const listener = () => {
        if (!modalVisible && api._modalVisible.value === true) {
          // button clicked
          const handler = eventHandlers.current.get(
            'click.honeepot-floating-button'
          );
          if (handler) {
            handler();
          }
          setModalVisible(true);
        } else if (modalVisible && api._modalVisible.value === false) {
          // modal closed
          setModalVisible(false);
        }
      };
      api.wrapperElement.addEventListener('click', listener);

      return () => {
        if (api?.wrapperElement) {
          api.wrapperElement.removeEventListener('click', listener);
        }
      };
    },
    [api, modalVisible]
  );

  useEffect(() => {
    return () => {
      window.clearInterval(sdkPollInterval.current);
    };
  }, []);

  // The honeepot api has implicit exit intent behavior, but only if the floating button is hidden.
  // To avoid conflicts, we show/hide our own wrapper instead.
  const patchApi = (
    honeepotApi: Api,
    wrapperElement: HTMLElement | null
  ): Api => {
    let showMethodTriggered = false;

    return Object.create(honeepotApi, {
      wrapperElement: {
        value: wrapperElement,
      },
      show: {
        value() {
          setModalVisible(true);
          showMethodTriggered = true;
          honeepotApi.show();
          setTimeout(() => {
            showMethodTriggered = false;
          });
        },
      },
      showFloatingButton: {
        value() {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- ...
          wrapperElement!.style.display = '';
        },
      },
      hideFloatingButton: {
        value() {
          if (!showMethodTriggered) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- ...
            wrapperElement!.style.display = 'none';
          }
        },
      },
    });
  };

  const load = useCallback<ContextValue['load']>(
    ({wrapperElement}) => {
      // just once
      if (sdkLoaded.current === true) {
        return;
      }
      const start = Date.now();
      sdkLoaded.current = true;
      // Load sdk
      const script = document.createElement('script');
      script.src = sdkUrl;
      script.async = true;

      if (config) {
        window.odysseusExitIntentConfig = config;
      }

      document.head.append(script);
      // we somehow need to discover that honeepot sdk finished loading
      // so we check every SDK_POLLING_INTERVAL milliseconds if window.odysseus is not null or undefined
      // we do this only up to SDK_TIMEOUT milliseconds
      sdkPollInterval.current = window.setInterval(() => {
        if (Date.now() - start >= SDK_TIMEOUT) {
          window.clearInterval(sdkPollInterval.current);
        } else if (window.odysseus != null) {
          window.clearInterval(sdkPollInterval.current);
          if (wrapperElement) {
            wrapHoneepotCustomElement(wrapperElement);
          }
          setApi(patchApi(window.odysseus, wrapperElement));
        }
      }, SDK_POLLING_INTERVAL);
    },
    [sdkUrl]
  );

  const on = useCallback<ContextValue['on']>((event, listener) => {
    eventHandlers.current.set(event, listener);
  }, []);

  const off = useCallback<ContextValue['off']>((event) => {
    eventHandlers.current.delete(event);
  }, []);

  const contextValue = useMemo(
    () => ({
      load,
      on,
      off,
      api,
      modalVisible,
    }),
    [load, on, off, api, modalVisible]
  );

  return (
    <HoneepotContext.Provider value={contextValue}>
      {children}
    </HoneepotContext.Provider>
  );
};
